import { formatDateRange, getWeekEnd, getWeekStart, isAfterDay, toChicagoDate } from "./eventDate";
import { transformEventToDisplayFormat } from "./eventFormatters";

const dailyEventData = {
  headline: "Daily",
  eventTitle: "Tours On Campus",
  eventDate: "See which tours are happening during your visit to the Center",
  eventPricing: "Free",
  cta: {
    text: "Learn more",
    url: "/visit/tours/spotlight-stories/"
  },
  image: {
    alt: "Campus Tours",
    src: "/mock/events/items/Tours_Hero.png"
  },
  theme: "lightOrange"
};

export const getItemsFromData = (data) => {
  const primaryEplEntry = data.pageEntry?.fields?.body?.content?.[0];
  const primaryEplEntryId = primaryEplEntry?.data?.target?.sys?.id || "";
  const calendarEpl = data.epls?.[primaryEplEntryId] || {};
  const {
    listItems = [],
    themedItems = [],
    featuredItems = [],
    startDate
  } = calendarEpl.fields || {};
  return {
    mappedListItems: listItems
      .map((item) => {
        return { ...item.fields, contentType: item.sys?.contentType?.sys?.id } || {};
      })
      .filter((item) => {
        // TODO: also filter end?
        return startDate ? isAfterDay(item.start, startDate) : true;
      }),
    mappedFeaturedItems: featuredItems.map((item) => {
      return { ...item.fields, contentType: item.sys?.contentType?.sys?.id } || {};
    }),
    mappedThemedItems: themedItems.map((item) => {
      return { ...item.fields, contentType: item.sys?.contentType?.sys?.id } || {};
    })
  };
};

const filterEventsForMonth = (events, monthValue) => {
  // Parse YYYY-MM-DD format correctly in local timezone
  const monthDate = toChicagoDate(monthValue);
  const targetYear = monthDate.getFullYear();
  const targetMonth = monthDate.getMonth();

  return events.filter((event) => {
    const eventDate = new Date(event.start);
    return eventDate.getFullYear() === targetYear && eventDate.getMonth() === targetMonth;
  });
};

const getFeaturedItemsForMonth = (featuredItems, monthValue) => {
  return filterEventsForMonth(featuredItems, monthValue);
};

export const transformEventItems = ({ items, themedItems, showMonth = false }) => {
  return items
    .map((event, index, arr) =>
      transformEventToDisplayFormat({
        event,
        themedItems,
        showMonth: showMonth ? isFirstOfMonth(event, index, arr) : false
      })
    )
    .filter(Boolean);
};

const isFirstOfMonth = (event, index, arr) => {
  if (!event || !event.start) {
    return false;
  }
  const eventDate = new Date(event.start);
  if (index === 0) {
    return true;
  }
  const prevEvent = arr[index - 1];
  if (!prevEvent || !prevEvent.start) {
    return true;
  }
  const prevDate = new Date(prevEvent.start);
  return (
    eventDate.getFullYear() !== prevDate.getFullYear() ||
    eventDate.getMonth() !== prevDate.getMonth()
  );
};
// TODO: this function and especially oraganizeEventsByMonth may be simplified b/c we only show one month at a time.
export const transformAndOrganizeEvents = ({
  selectedFormat,
  items,
  visibleMonths = [],
  themedItems = [],
  featuredItems = [],
  hasFiltersActive = false
}) => {
  if (!items || items.length === 0 || visibleMonths.length === 0) {
    return [];
  }

  if (selectedFormat === "view-as-grid") {
    return organizeEventsByMonth({
      items,
      visibleMonths,
      themedItems,
      featuredItems,
      hasFiltersActive
    });
  } else {
    return organizeEventsByWeek({
      items,
      visibleMonths,
      themedItems,
      featuredItems,
      hasFiltersActive
    });
  }
};

const organizeEventsByMonth = ({
  items,
  visibleMonths,
  themedItems,
  featuredItems,
  hasFiltersActive
}) => {
  const monthGroups = [];

  visibleMonths.forEach((monthValue) => {
    const monthDate = toChicagoDate(monthValue);
    const year = monthDate.getFullYear();
    const monthName = monthDate.toLocaleString("en-US", { month: "long" });

    const monthEvents = filterEventsForMonth(items, monthValue);
    const monthFeaturedItems = getFeaturedItemsForMonth(featuredItems, monthValue);

    const hasFeaturedItem = !hasFiltersActive && monthFeaturedItems.length > 0;
    const featuredItem = hasFeaturedItem ? monthFeaturedItems[0] : null;

    const sortedMonthEvents = sortEventsByDate(monthEvents);
    const combinedEvents = hasFeaturedItem
      ? [featuredItem, ...sortedMonthEvents.filter((e) => e.slug !== featuredItem.slug)]
      : sortedMonthEvents;

    const formattedItems = transformEventItems({
      items: combinedEvents,
      themedItems,
      showMonth: false
    });

    let finalItems = [];

    if (hasFiltersActive) {
      finalItems = formattedItems;
    } else if (hasFeaturedItem) {
      finalItems = [formattedItems[0], dailyEventData, ...formattedItems.slice(1)];
    } else {
      finalItems = [dailyEventData, ...formattedItems];
    }

    monthGroups.push({
      date: monthValue,
      title: `${monthName} ${year}`,
      items: finalItems,
      hasFeaturedItem: hasFeaturedItem || formattedItems.length <= 2
    });
  });

  return monthGroups;
};

const organizeEventsByWeek = ({
  items,
  visibleMonths,
  themedItems,
  featuredItems,
  hasFiltersActive
}) => {
  const weekGroups = [];

  const allMonthEvents = visibleMonths.flatMap((monthValue) =>
    filterEventsForMonth(items, monthValue)
  );

  const allMonthFeaturedItems = visibleMonths.flatMap((monthValue) =>
    getFeaturedItemsForMonth(featuredItems, monthValue)
  );

  const hasFeaturedItem = !hasFiltersActive && allMonthFeaturedItems.length > 0;

  if (!hasFiltersActive) {
    const firstFeaturedItem = hasFeaturedItem ? allMonthFeaturedItems[0] : null;
    const dailyWeekItems = hasFeaturedItem
      ? [
          ...transformEventItems({
            items: [firstFeaturedItem],
            themedItems,
            showMonth: true
          }),
          dailyEventData
        ]
      : [dailyEventData];

    weekGroups.push({
      heading: "Daily",
      items: dailyWeekItems,
      hasFeaturedItem
    });
  }

  const firstFeaturedItem = hasFeaturedItem ? allMonthFeaturedItems[0] : null;
  const sortedItems = sortEventsByDate(
    allMonthEvents.filter((item) => !firstFeaturedItem || item.slug !== firstFeaturedItem.slug)
  );

  const weekGroupsRaw = groupEventsByWeek(sortedItems);

  weekGroupsRaw.forEach((group) => {
    const weekEvents = group.events;

    const formattedItems = transformEventItems({
      items: weekEvents,
      themedItems,
      showMonth: true
    });

    weekGroups.push({
      heading: group.heading,
      items: formattedItems,
      hasFeaturedItem: false
    });
  });

  return weekGroups;
};

export const sortEventsByDate = (events) => {
  return [...events].sort((a, b) => new Date(a.start) - new Date(b.start));
};

export const groupEventsByWeek = (sortedEvents) => {
  const weekGroups = [];
  let currentWeek = [];
  let currentWeekStart = null;

  sortedEvents.forEach((event) => {
    const eventDate = toChicagoDate(event.start);

    if (!currentWeekStart || isAfterDay(eventDate, getWeekEnd(currentWeekStart))) {
      if (currentWeek.length > 0) {
        const isLastWeek = currentWeek.some((e) => e === sortedEvents[sortedEvents.length - 1]);
        const weekStartDate = currentWeekStart;
        const weekEndDate = isLastWeek
          ? toChicagoDate(currentWeek[currentWeek.length - 1].start)
          : getWeekEnd(weekStartDate);

        weekGroups.push({
          heading: formatDateRange(weekStartDate, weekEndDate),
          events: [...currentWeek],
          hasFeaturedItem: false
        });
      }

      currentWeekStart = getWeekStart(eventDate);
      currentWeek = [event];
    } else {
      currentWeek.push(event);
    }
  });

  // Add the last week if it has items
  if (currentWeek.length > 0) {
    const lastEventDate = toChicagoDate(currentWeek[currentWeek.length - 1].start);
    weekGroups.push({
      heading: formatDateRange(currentWeekStart, lastEventDate),
      events: [...currentWeek],
      hasFeaturedItem: false
    });
  }

  return weekGroups;
};
